import React, { useState, useRef, useEffect, useMemo } from "react";
import { scrollIntoViewWithEase } from "../../../helper/ScrollHelper";
import useClickOutside from "../TransactionHistory/useClickOutside";
import { ChevronDownIcon } from "../../icons";

// --- Custom Hooks & Helpers ---
// const useClickOutside = (ref, isOpen, handler) => {
//   useEffect(() => {
//     if (!isOpen) return;

//     const listener = (event) => {
//       if (!ref.current || ref.current.contains(event.target)) return;
//       handler(event);
//     };

//     document.addEventListener("mousedown", listener);
//     document.addEventListener("touchstart", listener);

//     return () => {
//       document.removeEventListener("mousedown", listener);
//       document.removeEventListener("touchstart", listener);
//     };
//   }, [ref, isOpen, handler]);
// };

// const scrollIntoViewWithEase = (element) => {
//   if (element && typeof element.scrollIntoView === "function") {
//     element.scrollIntoView({ behavior: "smooth", block: "nearest" });
//   }
// };

// --- SVG Icons ---
// const ChevronDownIcon = ({ size = 20, className = "" }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//     className={className}
//   >
//     <polyline points="6 9 12 15 18 9" />
//   </svg>
// );

// const ChevronIcon = ({ open, size = 20, className = "" }) => (
//   <ChevronDownIcon
//     size={size}
//     className={`${className} transition-transform duration-200 ${
//       open ? "rotate-180" : "rotate-0"
//     }`}
//   />
// );

// --- Component ---
export const CustomSearchDropdown = ({
  label,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error = "",
  placeholder = "Select an option",
  searchPlaceholder = "Search country...",
  className = "",
  showPhonecode = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  useClickOutside(containerRef, isOpen, closeDropdown);

  // Filter options across label, name, code, iso2, iso3, and phonecode
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options ?? [];

    return (options ?? []).filter((opt) =>
      [
        opt?.label,
        opt?.value,
        opt?.code,
        opt?.name,
        opt?.iso2,
        opt?.iso3,
        opt?.phonecode,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query)),
    );
  }, [options, search]);

  const handleSelect = (option) => {
    if (disabled) return;
    onChange?.(option);
    closeDropdown();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && filteredOptions.length > 0) {
      event.preventDefault();
      handleSelect(filteredOptions[0]);
    } else if (event.key === "Escape") {
      closeDropdown();
    }
  };

  useEffect(() => {
    if (isOpen && listRef.current) {
      scrollIntoViewWithEase(listRef.current);
    }
  }, [isOpen]);

  const OptionSkeleton = () => (
    <div className="animate-pulse rounded-[6px] bg-gray-100 dark:bg-zinc-800 px-4 py-3">
      <div className="h-4 w-32 rounded bg-gray-200 dark:bg-zinc-700" />
    </div>
  );

  return (
    <div ref={containerRef} className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
          {label}
        </label>
      )}

      <div className="relative">
        <div className="relative h-12">
          {/* Trigger (Closed State) */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen((prev) => !prev)}
            disabled={disabled}
            className={`absolute inset-0 flex h-12 w-full items-center justify-between rounded-[10px] border px-4 text-left custom-transition duration-200 ${
              isOpen
                ? "pointer-events-none scale-[0.98] opacity-0"
                : "scale-100 opacity-100"
            } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${
              error
                ? "border-red-500 bg-error-500/10"
                : "border-divider bg-background-surface"
            }`}
          >
            {value ? (
              <div className="flex items-center gap-3 truncate pr-2">
                {value.flag ? (
                  <img
                    src={value.flag}
                    alt={value.name || value.label || "flag"}
                    className="h-6 w-6 rounded-full object-cover shrink-0 shadow-sm"
                  />
                ) : (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-secondary text-[10px] font-bold text-text-primary uppercase">
                    {(value.iso2 || value.iso3 || value.name || "CO").slice(0, 2)}
                  </div>
                )}
                <span className="font-semibold text-sm text-text-secondary truncate">
                  {value.name || value.label} ({value.iso2.toUpperCase()})
                </span>
              </div>
            ) : (
              <span className="text-sm text-text-secondary truncate pr-2">
                {placeholder}
              </span>
            )}

            <ChevronDownIcon
              size={20}
              className={`shrink-0 text-text-secondary transition-transform duration-200 ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>

          {/* Search Input State */}
          <div
            ref={listRef}
            className={`absolute inset-0 flex h-12 items-center gap-2 rounded-[10px] border bg-background-surface px-4 custom-transition ${
              isOpen
                ? "scale-100 opacity-100"
                : "pointer-events-none scale-[0.98] opacity-0"
            } ${error ? "border-red-500 bg-error-500/10" : "border-divider"}`}
          >
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              disabled={loading}
              className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
            />

            <button
              type="button"
              onClick={closeDropdown}
              aria-label="Close options list"
            >
              <ChevronDownIcon
                size={20}
                className={`shrink-0 text-text-secondary transition-transform duration-200 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Dropdown Options List */}
        <div
          className={`absolute left-0 right-0 top-[calc(100%+4px)] z-40 max-h-[220px] overflow-y-auto rounded-[12px] border border-divider bg-background-surface p-2 shadow-[0_8px_24px_rgba(0,0,0,0.08)] hide-scrollbar origin-top custom-transition duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-y-100 opacity-100"
              : "pointer-events-none invisible -translate-y-2 scale-y-95 opacity-0"
          }`}
        >
          {loading ? (
            <div className="space-y-2 p-1">
              {Array.from({ length: 4 }).map((_, index) => (
                <OptionSkeleton key={index} />
              ))}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <p className="text-sm font-medium text-text-secondary">
                No results found
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {search ? "Try another search term." : "No options available."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredOptions.map((option) => {
                const isSelected =
                  (value?.id && value.id === option.id) ||
                  (value?.value && value.value === option.value) ||
                  (value?.iso2 && value.iso2 === option.iso2);

                return (
                  <button
                    key={option.id || option.value || option.iso2}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left transition-colors duration-150 custom-transition hover:bg-option-hover ${
                      isSelected
                        ? "bg-option-hover font-semibold text-text-primary"
                        : "bg-transparent text-text-primary"
                    }`}
                  >
                    <div className="flex items-center gap-3 truncate pr-2">
                      {option.flag ? (
                        <img
                          src={option.flag}
                          alt={option.name || option.label}
                          className="h-6 w-6 rounded-full object-cover shrink-0 shadow-sm"
                        />
                      ) : (
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-secondary text-[10px] font-bold text-text-secondary uppercase">
                          {(
                            option.iso2 ||
                            option.iso3 ||
                            option.name ||
                            "CO"
                          ).slice(0, 2)}
                        </div>
                      )}

                      <span className="text-sm truncate">
                        {option.name || option.label} ({option.iso2.toUpperCase()})
                      </span>
                    </div>

                    {showPhonecode && option.phonecode && (
                      <span className="text-xs text-text-secondary font-normal shrink-0">
                        {option.iso3.toUpperCase()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default CustomSearchDropdown;
